import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { ReportView } from '../ReportView';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import { MIDASProject } from '../../../types/project.types';
import { Report } from '../../../types/report.types';
import { validateMdsUrl } from '../../utils/urlValidator';
import { deserializeMDSFile } from '../../services/storageService';
import { initializeLoadedProject } from '../../services/projectService';
import { useProjectStore } from '../../stores/projectStore';
import { useProject } from '../../hooks/useProject';
import logger from '../../utils/logger';
import * as styles from './EmbedView.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface EmbedState {
  isLoading: boolean;
  error: string | null;
  project: MIDASProject | null;
  report: Report | null;
  /** 警告ダイアログを表示するか */
  showWarning: boolean;
  /** 警告の理由 */
  warningReason: string | null;
}

/**
 * EmbedView - 埋め込みモード専用のレポート表示コンポーネント
 *
 * 2つのモードをサポート:
 * 1. 外部MDSファイル: mdsパラメータでURLを指定
 * 2. ローカルプロジェクト: mdsパラメータなし（projectStoreから取得）
 *
 * URLパラメータ:
 * - mds: MDSファイルのURL（省略時はローカルプロジェクトを使用）
 * - report-id: レポートID
 * - report-name: レポート名（report-idが指定されていない場合に使用）
 * - embed: 埋め込みモードフラグ（このコンポーネントが使用される条件）
 */
export function EmbedView() {
  const [searchParams] = useSearchParams();
  const { projectId } = useParams<{ projectId: string }>();

  // projectStoreからプロジェクトを取得（ローカルプロジェクト用）
  const storeProject = useProjectStore((state) => state.project);

  const mdsUrl = searchParams.get('mds');

  // ローカルプロジェクトモードで、プロジェクトがストアにない場合は自動読み込み
  const shouldAutoLoad = !mdsUrl && projectId && (!storeProject || storeProject.id !== projectId);
  const { loading: projectLoading, error: projectError } = useProject(shouldAutoLoad ? projectId : '');

  const [state, setState] = useState<EmbedState>({
    isLoading: true,
    error: null,
    project: null,
    report: null,
    showWarning: false,
    warningReason: null,
  });
  const reportId = searchParams.get('report-id');
  const reportName = searchParams.get('report-name');

  // MDSファイルを読み込む（警告確認後に呼ばれる場合もある）
  const loadMds = useCallback(async () => {
    if (!mdsUrl) return;

    setState(prev => ({ ...prev, isLoading: true, showWarning: false }));

    try {
      logger.info('[EmbedView] Fetching MDS from URL:', mdsUrl);

      const response = await fetch(mdsUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // ファイルサイズチェック
      const contentLength = response.headers.get('Content-Length');
      if (contentLength && parseInt(contentLength, 10) > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds 10MB limit`);
      }

      const arrayBuffer = await response.arrayBuffer();
      if (arrayBuffer.byteLength > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds 10MB limit`);
      }

      logger.info('[EmbedView] MDS file downloaded:', arrayBuffer.byteLength, 'bytes');

      const data = new Uint8Array(arrayBuffer);
      const projectData = await deserializeMDSFile(data);
      const project = initializeLoadedProject(projectData);

      // ストアに保存（ReportViewが参照するため）
      useProjectStore.getState().loadProject(project);

      // レポートを検索
      let targetReport: Report | undefined;

      if (reportId) {
        targetReport = project.reports?.find((r) => r.id === reportId);
        if (!targetReport) {
          throw new Error(`Report not found: report-id="${reportId}"`);
        }
      } else if (reportName) {
        targetReport = project.reports?.find((r) => r.name === reportName);
        if (!targetReport) {
          throw new Error(`Report not found: report-name="${reportName}"`);
        }
      } else {
        // レポート指定がない場合、最初のレポートを使用
        logger.warn('[EmbedView] No report-id or report-name specified. Using first report as fallback.');
        targetReport = project.reports?.[0];
        if (!targetReport) {
          throw new Error('No reports found in project');
        }
      }

      logger.info('[EmbedView] Report loaded:', {
        reportId: targetReport.id,
        reportName: targetReport.name,
      });

      setState({
        isLoading: false,
        error: null,
        project,
        report: targetReport,
        showWarning: false,
        warningReason: null,
      });
    } catch (err) {
      logger.error('[EmbedView] Error loading MDS:', err);

      let errorMessage = 'Failed to load MDS file';
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Failed to fetch the MDS file. The server may not allow cross-origin requests (CORS).';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setState({
        isLoading: false,
        error: errorMessage,
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
    }
  }, [mdsUrl, reportId, reportName]);

  // ローカルプロジェクトモード: storeProjectからレポートを取得
  useEffect(() => {
    if (mdsUrl) return;

    // ルートパスでmdsパラメータがない場合（embed=trueだけ）
    if (!projectId) {
      setState({
        isLoading: false,
        error: 'Missing required parameter: mds',
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
      return;
    }

    // プロジェクト読み込み中
    if (projectLoading) {
      setState({
        isLoading: true,
        error: null,
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
      return;
    }

    // プロジェクト読み込みエラー
    if (projectError) {
      setState({
        isLoading: false,
        error: projectError,
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
      return;
    }

    if (!storeProject) {
      setState({
        isLoading: false,
        error: 'No project loaded',
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
      return;
    }

    // レポートを検索
    let targetReport: Report | undefined;

    if (reportId) {
      targetReport = storeProject.reports?.find((r) => r.id === reportId);
      if (!targetReport) {
        setState({
          isLoading: false,
          error: `Report not found: ${reportId}`,
          project: null,
          report: null,
          showWarning: false,
          warningReason: null,
        });
        return;
      }
    } else if (reportName) {
      targetReport = storeProject.reports?.find((r) => r.name === reportName);
      if (!targetReport) {
        setState({
          isLoading: false,
          error: `Report not found: ${reportName}`,
          project: null,
          report: null,
          showWarning: false,
          warningReason: null,
        });
        return;
      }
    } else {
      // レポート指定がない場合、最初のレポートを使用
      targetReport = storeProject.reports?.[0];
      if (!targetReport) {
        setState({
          isLoading: false,
          error: 'No reports found in project',
          project: null,
          report: null,
          showWarning: false,
          warningReason: null,
        });
        return;
      }
    }

    logger.info('[EmbedView] Local project report loaded:', {
      reportId: targetReport.id,
      reportName: targetReport.name,
    });

    setState({
      isLoading: false,
      error: null,
      project: storeProject,
      report: targetReport,
      showWarning: false,
      warningReason: null,
    });
  }, [mdsUrl, projectId, storeProject, reportId, reportName, projectLoading, projectError]);

  // 外部MDSモード: URL検証と読み込み開始
  useEffect(() => {
    if (!mdsUrl) return;

    // URL検証
    const validation = validateMdsUrl(mdsUrl);
    if (!validation.allowed) {
      setState({
        isLoading: false,
        error: validation.reason || 'URL is blocked for security reasons',
        project: null,
        report: null,
        showWarning: false,
        warningReason: null,
      });
      return;
    }

    // 警告が必要な場合はダイアログを表示
    if (validation.needsWarning) {
      logger.warn('[EmbedView] URL requires warning:', validation.reason);
      setState({
        isLoading: false,
        error: null,
        project: null,
        report: null,
        showWarning: true,
        warningReason: validation.reason || 'This URL is not from a trusted source',
      });
      return;
    }

    // ホワイトリストURL（警告なしで続行）
    loadMds();
  }, [mdsUrl, loadMds]);

  // 警告ダイアログでキャンセル
  const handleCancelWarning = useCallback(() => {
    logger.info('[EmbedView] User cancelled loading');
    setState({
      isLoading: false,
      error: 'Loading cancelled by user',
      project: null,
      report: null,
      showWarning: false,
      warningReason: null,
    });
  }, []);

  // 警告ダイアログで続行
  const handleContinueWarning = useCallback(() => {
    logger.info('[EmbedView] User accepted warning, continuing...');
    loadMds();
  }, [loadMds]);

  // ローディング状態
  if (state.isLoading) {
    return (
      <div className={styles.embedContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  // 警告ダイアログ
  if (state.showWarning) {
    return (
      <div className={styles.embedContainer}>
        <Dialog
          isOpen={true}
          onClose={handleCancelWarning}
          title="Confirm Report Source"
          width="md"
          closeOnOverlayClick={false}
          data-testid="embed-warning-dialog"
          footer={
            <>
              <Button variant="secondary" onClick={handleCancelWarning} data-testid="cancel-button">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleContinueWarning} data-testid="continue-button">
                Trust and Continue
              </Button>
            </>
          }
        >
          <div>
            <p style={{ fontSize: '15px', marginBottom: '12px', color: '#333' }}>
              <strong>Do you trust the publisher of this report?</strong>
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px', lineHeight: '1.5' }}>
              This report is loaded from an external source that is not verified by MIDAS.
              Only continue if you trust the website or person who shared this report with you.
            </p>
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '12px',
              borderRadius: '4px',
              wordBreak: 'break-all',
              fontSize: '12px',
              color: '#666',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>Source URL:</div>
              <div style={{ fontFamily: 'monospace' }}>{mdsUrl}</div>
            </div>
          </div>
        </Dialog>
        <div className={styles.attribution}>
          Created with{' '}
          <a
            href="https://app.midas-app.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            MIDAS
          </a>
        </div>
      </div>
    );
  }

  // エラー状態
  if (state.error) {
    return (
      <div className={styles.embedContainer}>
        <div className={styles.errorState}>
          <h3>Error</h3>
          <p>{state.error}</p>
        </div>
        <div className={styles.attribution}>
          Created with{' '}
          <a
            href="https://app.midas-app.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            MIDAS
          </a>
        </div>
      </div>
    );
  }

  // レポート表示
  return (
    <div className={styles.embedContainer}>
      <div className={styles.reportContent}>
        <ReportView
          project={state.project}
          report={state.report}
          readOnly={true}
        />
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          padding: '4px 8px',
          fontSize: '12px',
          color: '#666',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        Created with{' '}
        <a
          href="https://app.midas-app.org"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0066cc', textDecoration: 'none' }}
        >
          MIDAS
        </a>
      </div>
    </div>
  );
}
